import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'outline';
  size?: 'sm' | 'default' | 'lg' | 'icon';
}

const variants = {
  primary:
    'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 shadow-sm',
  secondary:
    'bg-secondary text-secondary-foreground hover:bg-background-muted border border-border',
  outline:
    'bg-transparent border border-border-strong text-foreground hover:bg-background-muted',
  ghost:
    'bg-transparent text-foreground hover:bg-background-muted',
  danger:
    'bg-red-600 text-white hover:bg-red-700 shadow-sm',
};

const sizes = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  default: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-base gap-2',
  icon: 'h-10 w-10',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { children, className = '', variant = 'primary', size = 'default', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      className={`
        inline-flex items-center justify-center rounded-md font-medium
        transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background
        disabled:pointer-events-none disabled:opacity-50
        whitespace-nowrap
        ${variants[variant]} ${sizes[size]} ${className}
      `}
      {...props}
    >
      {children}
    </button>
  );
});
